import { PropertyHintCoverageRow } from '~/services/ConnectionGoogleService';
import { Dayjs, toDayJs } from '~/services/TimeService';

// Fixed presentation bands, deliberately frontend-owned: changing them is a one-line
// edit with no API change, and age must be computed at render time — a backend-computed age
// would already be stale when the response lands.
export const AGE_BANDS = [
  { maxHours: 12, label: '<12h' },
  { maxHours: 24, label: '12–24h' },
  { maxHours: 48, label: '24–48h' },
  { maxHours: Infinity, label: '>48h' },
] as const;

export const CALENDAR_PAGE_DAYS = 28;

export function getAgeBandIndex(servedAt: string, now: Date): number {
  const ageHours = (now.getTime() - new Date(servedAt).getTime()) / (1000 * 60 * 60);
  return AGE_BANDS.findIndex((band) => ageHours < band.maxHours);
}

// Two units of precision (e.g. "16d 10h", "10h 5m"): enough to compare against the GHA price
// freshness column without implying a live-ticking clock — "now" is snapshotted on page load,
// so second-level precision would be stale the moment it renders.
export function formatAge(servedAt: string, now: Date): string {
  const ageMs = Math.max(0, now.getTime() - new Date(servedAt).getTime());
  const totalMinutes = Math.floor(ageMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// Finer-grained than AGE_BANDS on purpose: the summary bar answers "what is the freshness SHAPE
// of this property", so it needs resolution where the signal lives — around the 1–4h hint
// cooldowns at the fresh end. Everything older than 48h is one bucket: past that point it is
// simply "stale" and finer stale bands add noise, not signal (per Anson's review).
// The calendar keeps the coarser 4-band legend (cells are too small for 8 colours).
export const FRESHNESS_BUCKETS = [
  { maxHours: 1, label: '<1h' },
  { maxHours: 2, label: '1–2h' },
  { maxHours: 3, label: '2–3h' },
  { maxHours: 6, label: '3–6h' },
  { maxHours: 12, label: '6–12h' },
  { maxHours: 24, label: '12–24h' },
  { maxHours: 48, label: '24–48h' },
  { maxHours: Infinity, label: '>48h' },
] as const;

export interface FreshnessBucketCount {
  label: (typeof FRESHNESS_BUCKETS)[number]['label'];
  count: number;
  pct: number;
}

export const coverageCellKey = (checkInDate: string, los: number): string => `${checkInDate}:${los}`;

/**
 * The day the hint was generated on, in the ACCOUNT's timezone — derived exactly as
 * checkInDate − abw, so no client-side timezone knowledge is needed. This can differ from the
 * served_at date displayed in local (AEST) time: a serve at 01/07 00:05 AEST is still 30/06 in
 * UTC, so its ABW 36 hint lands on the same check-in date as an ABW 35 hint served later that
 * same AEST day. Surfacing this day is what makes those "duplicate" rows self-explanatory.
 */
export function hintDay(row: PropertyHintCoverageRow): string {
  return toDayJs(row.checkInDate).subtract(row.abw, 'day').format('YYYY-MM-DD');
}

export interface CoverageCell {
  checkInDate: string;
  los: number;
  /** Most recent serve among the rows resolving to this (checkInDate, los). */
  lastServedAt: string;
  /** All contributing rows, so tooltips can show the ABW(s)/tier(s) behind the cell. */
  rows: Array<PropertyHintCoverageRow>;
}

export interface PropertyCoverageDerived {
  /** Distinct (checkInDate, los) pairs — NOT rows.length (see overlap note below). */
  itineraryCount: number;
  lastHintServedAt: string | null;
  /**
   * Oldest serve among TODAY/FUTURE check-ins only (past check-ins never get re-hinted),
   * with the check-in date that stale hint belongs to so the card can name the itinerary.
   */
  oldestHint: { servedAt: string; checkInDate: string } | null;
  losValues: Array<number>;
  cellMap: Map<string, CoverageCell>;
  /** Hinted share of the default calendar window: next 28 days × the LOS values present. */
  next28Days: { covered: number; total: number; pct: number };
  /**
   * Price-freshness distribution: itineraries (distinct cells) bucketed by last-hint age,
   * aligned index-for-index with FRESHNESS_BUCKETS. Percentages are of itineraryCount.
   */
  freshness: Array<FreshnessBucketCount>;
  /** Itineraries (distinct cells) whose last hint is within 48h of now — the headline number. */
  freshWithin48h: { count: number; pct: number };
}

/**
 * Derive everything the property view renders from the raw coverage rows, in one place so the
 * summary cards, calendar, and itinerary list can never disagree.
 *
 * Overlap note two rows can resolve to the same (checkInDate, los) via different ABWs —
 * e.g. served yesterday at ABW 2 vs served today at ABW 1 both point at tomorrow. The calendar
 * cell takes max(servedAt), and itineraryCount counts distinct (checkInDate, los) pairs rather
 * than rows.length. The itinerary list still shows every row.
 */
export function derivePropertyCoverage(rows: Array<PropertyHintCoverageRow>, now: Date): PropertyCoverageDerived {
  const cellMap = new Map<string, CoverageCell>();
  const losSet = new Set<number>();
  // One definition of "today" for the whole derivation: the oldest-hint filter and the
  // next-28-days window (whose start IS today) must agree with the calendar's leftmost column.
  const todayStart = toDayJs(now).startOf('day');
  const todayStr = todayStart.format('YYYY-MM-DD');
  let lastHintServedAt: string | null = null;
  let oldestHint: { servedAt: string; checkInDate: string } | null = null;

  for (const row of rows) {
    losSet.add(row.los);
    if (lastHintServedAt === null || row.servedAt > lastHintServedAt) {
      lastHintServedAt = row.servedAt;
    }
    // Oldest only over today/future check-ins: past check-ins never get re-hinted, so they would
    // pin this at "ancient" forever and say nothing about how the sellable window is served.
    if (row.checkInDate >= todayStr && (oldestHint === null || row.servedAt < oldestHint.servedAt)) {
      oldestHint = { servedAt: row.servedAt, checkInDate: row.checkInDate };
    }

    const key = coverageCellKey(row.checkInDate, row.los);
    const cell = cellMap.get(key);
    if (!cell) {
      cellMap.set(key, { checkInDate: row.checkInDate, los: row.los, lastServedAt: row.servedAt, rows: [row] });
    } else {
      cell.rows.push(row);
      if (row.servedAt > cell.lastServedAt) {
        cell.lastServedAt = row.servedAt;
      }
    }
  }

  const losValues = Array.from(losSet).sort((a, b) => a - b);

  // Next-28-days coverage is scoped to the default calendar page (today..today+27 × the LOS
  // values present) so this number and the calendar the user first sees always agree.
  const windowEndStr = todayStart.add(CALENDAR_PAGE_DAYS, 'day').format('YYYY-MM-DD');
  let covered = 0;
  const bucketCounts = FRESHNESS_BUCKETS.map(() => 0);
  for (const cell of cellMap.values()) {
    if (cell.checkInDate >= todayStr && cell.checkInDate < windowEndStr) {
      covered++;
    }
    const ageHours = (now.getTime() - new Date(cell.lastServedAt).getTime()) / (1000 * 60 * 60);
    bucketCounts[FRESHNESS_BUCKETS.findIndex((bucket) => ageHours < bucket.maxHours)]++;
  }
  const next28Total = CALENDAR_PAGE_DAYS * losValues.length;
  const freshWithin48hCount = bucketCounts.slice(0, -1).reduce((sum, count) => sum + count, 0);

  return {
    itineraryCount: cellMap.size,
    lastHintServedAt,
    oldestHint,
    losValues,
    cellMap,
    next28Days: {
      covered,
      total: next28Total,
      pct: next28Total > 0 ? Math.round((covered / next28Total) * 100) : 0,
    },
    freshness: FRESHNESS_BUCKETS.map((bucket, i) => ({
      label: bucket.label,
      count: bucketCounts[i],
      // One decimal: whole-number rounding turns a handful of fresh itineraries among
      // thousands into a misleading 0%.
      pct: cellMap.size > 0 ? Math.round((bucketCounts[i] / cellMap.size) * 1000) / 10 : 0,
    })),
    // Everything except the last (>48h) bucket — kept as a card alongside the distribution
    // because it is the single number people quote.
    freshWithin48h: {
      count: freshWithin48hCount,
      pct: cellMap.size > 0 ? Math.round((freshWithin48hCount / cellMap.size) * 100) : 0,
    },
  };
}

/**
 * Calendar page (28 days per page) containing a check-in date, relative to page 0 starting today.
 * Negative pages are older dates.
 */
export function calendarPageForDate(checkInDate: string, today: Dayjs): number {
  const diffDays = toDayJs(checkInDate).diff(today.startOf('day'), 'day');
  return Math.floor(diffDays / CALENDAR_PAGE_DAYS);
}

export function calendarDatesForPage(page: number, today: Dayjs): Array<string> {
  const start = today.startOf('day').add(page * CALENDAR_PAGE_DAYS, 'day');
  return Array.from({ length: CALENDAR_PAGE_DAYS }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
}
